import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Suspense, lazy, useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import ScrollProgress from "@/components/ScrollProgress";
import LandingPage from "@/pages/LandingPage";

// Lazy-loaded pages for bundle size optimization
const NewsPage = lazy(() => import("@/pages/NewsPage"));
const NewsDetailPage = lazy(() => import("@/pages/NewsDetailPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const EventDetailPage = lazy(() => import("@/pages/EventDetailPage"));
const TeamPage = lazy(() => import("@/pages/TeamPage"));
const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const RulesPage = lazy(() => import("@/pages/RulesPage"));
const DriverPage = lazy(() => import("@/pages/DriverPage"));
const ApplyPage = lazy(() => import("@/pages/ApplyPage"));
const JobDetailPage = lazy(() => import("@/pages/JobDetailPage"));
const PublicMapPage = lazy(() => import("@/pages/PublicMapPage"));
const TrackerPage = lazy(() => import("@/pages/TrackerPage"));

// Management - Lazy Loaded
const MgmtLoginPage = lazy(() => import("@/drivershub/pages/MgmtLoginPage"));
const MgmtRegisterPage = lazy(() => import("@/drivershub/pages/MgmtRegisterPage"));
const MgmtHomePage = lazy(() => import("@/drivershub/pages/MgmtHomePage"));
const MgmtNewsPage = lazy(() => import("@/drivershub/pages/MgmtNewsPage"));
const MgmtEventsPage = lazy(() => import("@/drivershub/pages/MgmtEventsPage"));
const MgmtTeamPage = lazy(() => import("@/drivershub/pages/MgmtTeamPage"));
const MgmtStatsPage = lazy(() => import("@/drivershub/pages/MgmtStatsPage"));
const MgmtChatPage = lazy(() => import("@/drivershub/pages/MgmtChatPage"));
const MgmtAdminPage = lazy(() => import("@/drivershub/pages/MgmtAdminPage"));
const MgmtGalleryPage = lazy(() => import("@/drivershub/pages/MgmtGalleryPage"));
const MgmtProfilePage = lazy(() => import("@/drivershub/pages/MgmtProfilePage"));
const MgmtApplicationsPage = lazy(() => import("@/drivershub/pages/MgmtApplicationsPage"));
const MgmtReportsPage = lazy(() => import("@/drivershub/pages/MgmtReportsPage"));
const MgmtMapPage = lazy(() => import("@/drivershub/pages/MgmtMapPage"));

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { HubThemeProvider } from "@/context/HubThemeContext";
import ManagementLayout from "@/drivershub/components/ManagementLayout";
import { motion, AnimatePresence } from "framer-motion";



const MapFallback = () => (
  <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] bg-transparent">
    <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
  </div>
);

const NAV_ORDER = [
  "/",
  "/news",
  "/events",
  "/team",
  "/gallery",
  "/apply"
];

const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.18,
      ease: [0.16, 1, 0.3, 1]
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.1,
      ease: [0.32, 0, 0.67, 0]
    }
  }
};

const noAnimation = {
  initial: { opacity: 1 },
  animate: { opacity: 1 },
  exit: { opacity: 1 },
};


const STATIC_PAGE_BGS = [
  '/images/home.webp',
  '/images/news.webp',
  '/images/events.webp',
  '/images/team.webp',
  '/images/gallery.webp',
  '/images/apply.webp',
];

const getPageBackground = (pathname) => {
  const path = (pathname || "").toLowerCase().replace(/\/+$/, '') || '/';
  if (
    path.startsWith('/driver') ||
    path.startsWith('/fahrer') ||
    path.startsWith('/job') ||
    path.includes('/karte') ||
    path.includes('/map') ||
    path.startsWith('/drivershub')
  ) {
    return null;
  }
  if (path.startsWith('/news')) return '/images/news.webp';
  if (path.startsWith('/events')) return '/images/events.webp';
  if (path.startsWith('/team')) return '/images/team.webp';
  if (path.startsWith('/gallery') || path.startsWith('/galerie')) return '/images/gallery.webp';
  if (path.startsWith('/apply') || path.startsWith('/bewerben')) return '/images/apply.webp';
  if (path === '' || path === '/' || path.startsWith('/regelwerk') || path.startsWith('/tracker')) {
    return '/images/home.webp';
  }
  return '/images/home.webp';
};

function PageBackground({ src, isDrivershub }) {
  const [current, setCurrent] = useState(src);
  const [previous, setPrevious] = useState(null);
  const currentRef = useRef(current);
  currentRef.current = current;

  useEffect(() => {
    const prevVal = currentRef.current;
    if (src === prevVal) return;

    if (!src) {
      // Smoothly fade out the previous background into black instead of an abrupt flash
      setPrevious(prevVal);
      setCurrent(null);
      const timer = setTimeout(() => {
        setPrevious(null);
      }, 450);
      return () => clearTimeout(timer);
    }

    setPrevious(prevVal);
    setCurrent(src);

    const timer = setTimeout(() => {
      setPrevious(null);
    }, 450);

    return () => clearTimeout(timer);
  }, [src]);

  const overlayGradient = isDrivershub
    ? 'rgba(0,0,0,0.88), rgba(0,0,0,0.88)'
    : 'rgba(0,0,0,0.55), rgba(0,0,0,0.55)';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#000',
        zIndex: -1,
        pointerEvents: 'none',
        overflow: 'hidden',
        transform: 'translateZ(0)',
      }}
    >
      {previous && (
        <div
          key={`prev-${previous}`}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(${overlayGradient}), url(${previous})`,
            backgroundColor: '#000',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 1,
            transform: 'translateZ(0)',
          }}
        />
      )}
      {current && (
        <motion.div
          key={`curr-${current}`}
          initial={{ opacity: previous ? 0 : 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(${overlayGradient}), url(${current})`,
            backgroundColor: '#000',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'translateZ(0)',
          }}
        />
      )}
    </div>
  );
}

function AppContent() {


  const { isAdmin } = useAuth();
  const location = useLocation();
  const isDrivershub = location.pathname.startsWith('/drivershub');
  const isJobDetail = location.pathname.startsWith('/job/');
  const isEventDetail = location.pathname.startsWith('/events/') && location.pathname !== '/events';
  const isNewsDetail = location.pathname.startsWith('/news/') && location.pathname !== '/news';
  const isPublicMap = location.pathname === '/map' || location.pathname === '/karte';
  const hideNavbar = isDrivershub || isJobDetail || isEventDetail || isNewsDetail || isPublicMap;

  // Preload all static backgrounds on initial mount so transitions never wait for network requests
  useEffect(() => {
    STATIC_PAGE_BGS.forEach((src) => {
      const img = new Image();
      img.src = src;
    });

    // Idle prefetch route chunks so navigation never triggers Suspense
    const preloadRoutes = () => {
      import("@/pages/LandingPage").catch(() => {});
      import("@/pages/NewsPage").catch(() => {});
      import("@/pages/EventsPage").catch(() => {});
      import("@/pages/TeamPage").catch(() => {});
      import("@/pages/DriverPage").catch(() => {});
      import("@/pages/JobDetailPage").catch(() => {});
      import("@/pages/GalleryPage").catch(() => {});
      import("@/pages/RulesPage").catch(() => {});
      import("@/pages/ApplyPage").catch(() => {});
      import("@/drivershub/components/ManagementLayout").catch(() => {});
      import("@/drivershub/pages/MgmtHomePage").catch(() => {});
      import("@/drivershub/pages/MgmtLoginPage").catch(() => {});
    };

    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(preloadRoutes);
      } else {
        setTimeout(preloadRoutes, 1200);
      }
    }
  }, []);

  // Synchronously compute current background for the exact active route
  const currentBg = useMemo(() => getPageBackground(location.pathname), [location.pathname]);

  // Persistent shell key: Keep DriversHub layout mounted when switching tabs within DriversHub
  const shellKey = isDrivershub ? "drivershub-root" : location.pathname;

  // Hide the native scrollbar on public pages (the scroll progress bar acts as
  // the scroll affordance). The drivershub keeps its styled scrollbar.
  useEffect(() => {
    const root = document.documentElement;
    if (isDrivershub) {
      root.classList.remove("no-scrollbar");
    } else {
      root.classList.add("no-scrollbar");
    }
  }, [isDrivershub]);


  // Instant scroll-to-top BEFORE browser paint to prevent any visual jump/twitch
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <PageBackground src={currentBg} isDrivershub={isDrivershub} />
      {!isDrivershub && <ScrollProgress />}
      <AnimatePresence>
        {!hideNavbar && (
          <motion.div
            key="public-navbar-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <Navbar />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait">
        <motion.div
          key={shellKey}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <Suspense fallback={<MapFallback />}>
            <Routes location={location}>
              {/* Public Website */}
              <Route path="/*" element={
                <div
                  className="relative min-h-screen"
                >
                  <div className="relative">
                    <div
                      className={`w-full flex flex-col justify-between ${hideNavbar ? 'min-h-screen' : 'min-h-[calc(100vh-4rem)]'}`}
                    >
                      <div className="flex-grow">
                        <Routes location={location}>
                          <Route path="/" element={<LandingPage />} />
                          <Route path="/news" element={<NewsPage />} />
                          <Route path="/news/:id" element={<NewsDetailPage />} />
                          <Route path="/events" element={<EventsPage />} />
                          <Route path="/events/:slug" element={<EventDetailPage />} />
                          <Route path="/team" element={<TeamPage />} />
                          <Route path="/driver/:id" element={<DriverPage />} />
                          <Route path="/job/:jobId" element={<JobDetailPage />} />
                          <Route path="/gallery" element={<GalleryPage />} />
                          <Route path="/regelwerk" element={<RulesPage />} />
                          <Route path="/tracker" element={<TrackerPage />} />
                          <Route path="/apply" element={<ApplyPage />} />
                          <Route path="/map" element={<PublicMapPage />} />
                          <Route path="/karte" element={<PublicMapPage />} />
                        </Routes>
                      </div>
                      {location.pathname !== '/map' && location.pathname !== '/karte' && (
                        <Footer
                          variant={
                            location.pathname === '/' ||
                            location.pathname.startsWith('/job/') ||
                            location.pathname.startsWith('/driver/') ||
                            location.pathname.startsWith('/fahrer/') ||
                            location.pathname.startsWith('/events/')
                              ? 'black'
                              : 'transparent'
                          }
                          withGradient={location.pathname === '/'}
                        />
                      )}
                    </div>
                  </div>
                </div>
              } />

              {/* Management - Public Auth Pages */}
              <Route path="/drivershub/login" element={<MgmtLoginPage />} />
              <Route path="/drivershub/register" element={<MgmtRegisterPage />} />

              {/* Management - Protected */}
              <Route path="/drivershub" element={<ManagementLayout />}>
                <Route index element={<Navigate to="/drivershub/home" replace />} />
                <Route path="home" element={<MgmtHomePage />} />
                <Route path="news" element={<MgmtNewsPage />} />
                <Route path="events" element={<MgmtEventsPage />} />
                <Route path="team" element={<MgmtTeamPage />} />
                <Route path="statistiken" element={<MgmtStatsPage />} />
                <Route path="galerie" element={<MgmtGalleryPage />} />
                <Route path="karte" element={<MgmtMapPage />} />
                <Route path="chat" element={<MgmtChatPage />} />
                <Route path="admin" element={<MgmtAdminPage />} />
                <Route path="profil" element={<MgmtProfilePage />} />
                <Route path="fahrer/:id" element={<MgmtProfilePage />} />
                <Route path="bewerbungen" element={<MgmtApplicationsPage />} />
                <Route path="berichte" element={<MgmtReportsPage />} />
              </Route>
            </Routes>
          </Suspense>
        </motion.div>
      </AnimatePresence>

    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <HubThemeProvider>
          <AppContent />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "rgba(10, 10, 10, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#F8FAFC",
              },
            }}
          />
        </HubThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;